import {AlertFillIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ReviewState, type CompletedReview} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useCallback, useRef} from 'react'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {useDockedPanelActions} from '../../../../contexts/DockedPanelContext'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

export function shouldShowFeedbackOpen(review: CompletedReview | undefined, reviewThreads: Thread[]): boolean {
  const hasFeedbackReview = review?.state === ReviewState.COMMENTED || review?.state === ReviewState.CHANGES_REQUESTED
  return hasFeedbackReview && reviewThreads?.some(thread => !thread.isResolved)
}

export function FeedbackOpen({variant}: {variant?: 'default' | 'embedded'}) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dockedPanelActions = useDockedPanelActions()
  const currentUserLogin = getCurrentUserLogin()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const handleOpen = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'feedback-open',
      action_name: 'view_comments',
    })
    dockedPanelActions.openPanel('comments', {
      returnFocusTo: buttonRef.current,
      metadata: {selectedAuthor: currentUserLogin, hideResolved: true},
    })
  }, [currentUserLogin, dockedPanelActions, sendPullRequestAnalyticsEvent])

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={AlertFillIcon} emphasized variant="warning" />}
      title="Awaiting the author"
      itemName="feedback-open"
      variant={variant}
      description="Nudge the author to address your open comments."
      emphasizedLeadingVisual
      actionSlot={
        <Button ref={buttonRef} size="small" onClick={handleOpen}>
          View your comments
        </Button>
      }
    />
  )
}
